import {
  LayoutModule,
  ModuleProperties,
  scrollTo,
  useAnimationStates,
  useRenderedChildren,
  useShowInstructions,
} from '@backstage-components/base';
import {Box, BoxProps} from '@chakra-ui/react';
import {css, cx} from '@emotion/css';
import {motion} from 'framer-motion';
import {useSubscription} from 'observable-hooks';
import {Fragment, FC, MouseEvent} from 'react';
import {
  ComponentDefinition,
  reactName as namespace,
  SchemaType,
} from './ButtonOverlayDefinition';

export interface ButtonOverlayProps extends ModuleProperties, SchemaType {
  onClick?: () => void;
  isExternal?: boolean;
}

export type ButtonOverlayComponentDefinition = LayoutModule<
  'ButtonOverlay',
  ButtonOverlayProps
>;

export const TEST_ID = `${namespace}-test`;

const MotionBox = motion<BoxProps>(Box);
const MotionLink = motion<
  BoxProps & Pick<HTMLAnchorElement, 'href' | 'target'>
>(Box);

export const ButtonOverlayComponent: FC<ButtonOverlayComponentDefinition> = (
  definition
) => {
  const {slotRenderer: Component = () => <Fragment />} = definition;
  const {props} = definition;
  const {animationStates, href} = props;
  const renderedChildren = useRenderedChildren(Component, definition.slots);

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  const motionOptions = useAnimationStates(
    observable,
    broadcast,
    animationStates
  );

  const onClick = (e?: MouseEvent): void => {
    if ('onClick' in props && typeof props.onClick === 'function') {
      props.onClick();
    }
    if (href?.startsWith('/')) {
      e?.preventDefault();
    }
    const meta = href ? {href} : {};
    broadcast({type: `${namespace}:on-click`, meta});
  };

  useSubscription(observable, (inst) => {
    if (inst.type === `${namespace}:click`) {
      onClick();
    } else if (inst.type === `ButtonOverlay:scrollTo`) {
      const {elementId, anchorElId, scrollX, scrollY} = inst.meta;
      scrollTo({elementId, anchorElId, scrollX, scrollY});
    }
  });

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  return href ? (
    <MotionLink
      as="a"
      role="link"
      href={href}
      target={props?.isExternal ? '_blank' : '_self'}
      id={definition.id}
      data-testid={TEST_ID}
      className={cx(styleClassName, definition.mid)}
      {...motionOptions}
      onClick={onClick}
      onFocus={() => broadcast({type: 'ButtonOverlay:on-focus', meta: {}})}
      onMouseEnter={() =>
        broadcast({type: 'ButtonOverlay:on-mouseenter', meta: {}})
      }
      onMouseLeave={() =>
        broadcast({type: 'ButtonOverlay:on-mouseleave', meta: {}})
      }
    >
      {renderedChildren}
    </MotionLink>
  ) : (
    <MotionBox
      as="button"
      role="button"
      id={definition.id}
      data-testid={TEST_ID}
      className={cx(styleClassName, definition.mid)}
      {...motionOptions}
      onClick={onClick}
      onFocus={() => broadcast({type: 'ButtonOverlay:on-focus', meta: {}})}
      onMouseEnter={() =>
        broadcast({type: 'ButtonOverlay:on-mouseenter', meta: {}})
      }
      onMouseLeave={() =>
        broadcast({type: 'ButtonOverlay:on-mouseleave', meta: {}})
      }
    >
      {renderedChildren}
    </MotionBox>
  );
};
